import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./FullReviews.scss";
import star from "../Components/images/star.svg";
import greyStar from "../Components/images/star-grey.svg";
import whiteStar from "../Components/images/star-white.svg";
import { useParams } from "react-router-dom";
import leftArrowWhite from '../Components/images/assets/left-arrow-white.svg'
import rightArrowWhite from '../Components/images/assets/right-arrow-white.svg'
import _ from "lodash";
import { API_BASE_URL } from '../config';
import { ensureMediaUrl } from '../utils/mediaUrl';

const BASE_URL = API_BASE_URL;

export function FullReviews() {

    const { product_id } = useParams();
    console.log('full review product_id - ', product_id);

    const [ratingMeta, setRatingMeta] = useState({})
    const [product, setProduct] = useState(null)
    const [modalImages, setModalImages] = useState([])
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [modalImagesList, setModalImagesList] = useState([])
    const [modalCurrentIndex, setModalCurrentIndex] = useState(0)
    const [page, setPage] = useState(1)
    const observer = useRef();
    const [loading, setLoading] = useState(false);
    const [noMoreProducts, setNoMoreProducts] = useState(false);
    const firstUrl = product?.urls?.[0];
    const productImageUrl = firstUrl
        ? (firstUrl.startsWith("http") ? firstUrl : `${BASE_URL}/${firstUrl.replace(/^\//, "")}`)
        : ensureMediaUrl(ratingMeta?.reviews_data?.[0]?.images?.[0] || null);
    const productBrand = Array.isArray(product?.brand) ? product?.brand?.[0] : product?.brand;
    const productTitle = product?.title;
    const productPrice = product?.price;
    const productMrp = product?.mrp;
    const productDiscount = productMrp != null && productPrice != null && productMrp > 0 ? Math.round((1 - productPrice / productMrp) * 100) : 0;
    const customerPhotosFlat = useMemo(() => {
        const out = [];
        const source =
            (modalImages || []).length > 0
                ? modalImages
                : ratingMeta?.reviews_data || [];
        (source || []).forEach((review_data, reviewIndex) => {
            (review_data?.images || []).forEach((url, imageIndex) => {
                out.push({ url: ensureMediaUrl(url), reviewIndex, imageIndex, i_order: review_data?.i_order });
            });
        });
        return out;
    }, [modalImages, ratingMeta]);
    const totalCustomerPhotos = customerPhotosFlat.length;
    const CUSTOMER_PHOTOS_DISPLAY_LIMIT = 14; // ~2 rows
    const customerPhotosToShow = customerPhotosFlat.slice(0, CUSTOMER_PHOTOS_DISPLAY_LIMIT);
    const allPhotosFlat = useMemo(
        () => customerPhotosFlat.map((p) => p.url),
        [customerPhotosFlat]
    );

    const openImageModal = (imagesList, index) => {
        if (!imagesList?.length) return;
        setModalImagesList(imagesList);
        setModalCurrentIndex(index);
        setImageModalOpen(true);
    };
    const closeImageModal = () => setImageModalOpen(false);
    const modalPrev = () => setModalCurrentIndex((i) => Math.max(0, i - 1));
    const modalNext = () => setModalCurrentIndex((i) => Math.min(modalImagesList.length - 1, i + 1));
    const modalCurrentUrl = modalImagesList[modalCurrentIndex] || null;
    const canModalPrev = modalCurrentIndex > 0;
    const canModalNext = modalCurrentIndex < modalImagesList.length - 1;

    useEffect(() => {
        if (!imageModalOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeImageModal();
            if (e.key === 'ArrowLeft' && canModalPrev) modalPrev();
            if (e.key === 'ArrowRight' && canModalNext) modalNext();
        };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [imageModalOpen, canModalPrev, canModalNext]);

    useEffect(() => {
        if (!product_id) return;
        fetchModalImages();
    }, [product_id])

    useEffect(() => {
        if (!product_id) return;
        const token = localStorage.getItem("token");
        const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
        axios.post(`${BASE_URL}/products/specifications/${product_id}`, {}, config)
            .then((res) => setProduct(res.data || null))
            .catch(() => setProduct(null));
    }, [product_id])

    const prevProductIdRef = useRef(product_id);
    useEffect(() => {
        if (!product_id) return;
        if (prevProductIdRef.current !== product_id) {
            prevProductIdRef.current = product_id;
            setPage(1);
            setRatingMeta({});
            setNoMoreProducts(false);
            fetchProductSpecifications(1);
        } else {
            fetchProductSpecifications();
        }
    }, [product_id, page])

    const debouncedLoadMore = useCallback(
        _.debounce(() => {
            if (!noMoreProducts) {
                setPage((prevPage) => prevPage + 1);
            }
        }, 300),
        [noMoreProducts]
    );

    const lastProductRef = useCallback((node) => {
        console.log('in lastProductRef');

        if (loading || noMoreProducts) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                debouncedLoadMore();
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, debouncedLoadMore, noMoreProducts]);

    async function fetchModalImages() {
        try {
            const token = localStorage.getItem("token");
            const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };

            const response = await axios.post(
                `${BASE_URL}/products/modal-images/${product_id}`,
                { page: 1, size: 20 },
                config
            );

            setModalImages(Array.isArray(response.data) ? response.data : []);
        }
        catch (error) {
            console.error("Failed to fetch modal images:", error);
            setModalImages([]);
        }
    }

    async function fetchProductSpecifications(pageOverride) {
        const pageToUse = pageOverride ?? page;
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.post(
                `/products/ratings-meta/${product_id}`,
                {
                    page: pageToUse,
                    size: 10
                },
                config
            );

            const data = Array.isArray(response.data) ? null : response.data;
            const newReviews = Array.isArray(data?.reviews_data) ? data.reviews_data : [];
            const size = 10;

            setRatingMeta((prev) => {
                if (pageToUse === 1) {
                    return data && typeof data === 'object' ? { ...data } : {};
                }
                if (!prev?.product_id || !newReviews.length) {
                    return prev;
                }
                const existingIds = new Set((prev.reviews_data || []).map((r) => r?._id).filter(Boolean));
                const appended = (prev.reviews_data || []).concat(
                    newReviews.filter((r) => r?._id && !existingIds.has(r._id))
                );
                return { ...prev, reviews_data: appended };
            });

            setNoMoreProducts(newReviews.length < size);
        }
        catch (error) {
            console.error("Failed to fetch products:", error);
            setNoMoreProducts(true);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="full-review-container">
            <div className="detailed-reviews-flexProduct">
                <div className="product-details-container">
                    <div className="product-details-imageWrapper" >
                        <div
                            className="product-details-image"
                            style={{ backgroundImage: productImageUrl ? `url(${productImageUrl})` : undefined }}
                        ></div>
                        <div className="skeleton-skeletonLoader"></div>
                    </div>
                    <h1 className="product-details-brand">{productBrand ?? '—'}</h1>
                    <h1 className="product-details-name">
                        {productTitle ?? 'Product'}
                    </h1>
                    {(productPrice != null || productMrp != null) && (
                        <p className="product-details-discountContainer">
                            {productPrice != null && (
                                <span className="product-details-price">
                                    <strong>Rs. {productPrice}</strong>
                                </span>
                            )}
                            {productMrp != null && productMrp > 0 && (
                                <span className="product-details-mrp">
                                    <s>Rs. {productMrp}</s>
                                </span>
                            )}
                            {productDiscount > 0 && (
                                <span className="product-details-discount">({productDiscount}% OFF)</span>
                            )}
                        </p>
                    )}
                </div>
            </div>

            <div className="reviews-ratings-container">
                {/* <div className="detailedRatingContainer">
                    <div className="headingContainer">
                        Ratings
                        <span className="myntraweb-sprite index-productRatingsIcon sprites-productRatingsIcon"></span>
                    </div>
                    <div className="index-flexRow index-margin22">
                        <div className="index-flexColumn">
                            <div className="index-flexRow index-averageRating">
                                <span>3.6</span>
                                <span className="big-star">
                                    <img src={star} />
                                </span>
                            </div>
                            <div className="index-countDesc">514 Verified Buyers</div>
                        </div>
                        <div className="index-separator"></div>
                        <div className="progress-container">
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">5</span>
                                    <span>
                                        <img src={greyStar} />
                                    </span>
                                </div>
                                <progress
                                    min="0"
                                    max="514"
                                    value="235"
                                    data-rating="5"
                                ></progress>
                                <div className="index-count">235</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">4</span>
                                    <span>
                                        <img src={greyStar} />
                                    </span>
                                </div>
                                <progress
                                    min="0"
                                    max="514"
                                    value="89"
                                    data-rating="4"
                                ></progress>
                                <div className="index-count">89</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">3</span>
                                    <span>
                                        <img src={greyStar} />
                                    </span>
                                </div>
                                <progress
                                    min="0"
                                    max="514"
                                    value="59"
                                    data-rating="3"
                                ></progress>
                                <div className="index-count">59</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">2</span>
                                    <span>
                                        <img src={greyStar} />
                                    </span>
                                </div>
                                <progress
                                    min="0"
                                    max="514"
                                    value="35"
                                    data-rating="2"
                                ></progress>
                                <div className="index-count">35</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">1</span>
                                    <span>
                                        <img src={greyStar} />
                                    </span>
                                </div>
                                <progress
                                    min="0"
                                    max="514"
                                    value="96"
                                    data-rating="1"
                                ></progress>
                                <div className="index-count">96</div>
                            </div>
                        </div>
                    </div>
                </div> */}

                <div className="detailedRatingContainer design-4">
                    <div className="headingContainer">
                        Ratings
                        <span className="myntraweb-sprite index-productRatingsIcon sprites-productRatingsIcon"></span>
                    </div>
                    <div className="index-flexRow index-margin22 review-container">
                        <div className="index-flexColumn">
                            <div className="index-flexRow index-averageRating">
                                <span>{ratingMeta?.ratings_average}</span>
                                <span className="big-star"><img src={star} alt="star" /></span>
                            </div>
                            <div className="index-countDesc">{ratingMeta?.total_ratings_count} Verified Buyers</div>
                        </div>
                        <div className="index-separator"></div>
                        <div className="progress-container progress-variant-1">
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">Excellent</span>
                                </div>
                                <div className="progress-wrapper" style={{
                                    '--progress-percent': ratingMeta?.total_ratings_count > 0 ? `${(ratingMeta?.specific_rating_count?.[4] / ratingMeta?.total_ratings_count) * 100}%` : '0%'
                                }}>
                                    <progress min="0" max={ratingMeta?.total_ratings_count} value={ratingMeta?.specific_rating_count?.[4]} data-rating="5"></progress>
                                    <span className="progress-star" data-rating="5"><img src={star} alt="star" /></span>
                                </div>
                                <div className="index-count">{ratingMeta?.specific_rating_count?.[4] ?? 0}</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">Very Good</span>
                                </div>
                                <div className="progress-wrapper" style={{
                                    '--progress-percent': ratingMeta?.total_ratings_count > 0 ? `${(ratingMeta?.specific_rating_count?.[3] / ratingMeta?.total_ratings_count) * 100}%` : '0%'
                                }}>
                                    <progress min="0" max={ratingMeta?.total_ratings_count} value={ratingMeta?.specific_rating_count?.[3]} data-rating="4"></progress>
                                    <span className="progress-star" data-rating="4"><img src={star} alt="star" /></span>
                                </div>
                                <div className="index-count">{ratingMeta?.specific_rating_count?.[3] ?? 0}</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">Good</span>
                                </div>
                                <div className="progress-wrapper" style={{
                                    '--progress-percent': ratingMeta?.total_ratings_count > 0 ? `${(ratingMeta?.specific_rating_count?.[2] / ratingMeta?.total_ratings_count) * 100}%` : '0%'
                                }}>
                                    <progress min="0" max={ratingMeta?.total_ratings_count} value={ratingMeta?.specific_rating_count?.[2]} data-rating="3"></progress>
                                    <span className="progress-star" data-rating="3"><img src={star} alt="star" /></span>
                                </div>
                                <div className="index-count">{ratingMeta?.specific_rating_count?.[2] ?? 0}</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">Not Bad</span>
                                </div>
                                <div className="progress-wrapper" style={{
                                    '--progress-percent': ratingMeta?.total_ratings_count > 0 ? `${(ratingMeta?.specific_rating_count?.[1] / ratingMeta?.total_ratings_count) * 100}%` : '0%'
                                }}>
                                    <progress min="0" max={ratingMeta?.total_ratings_count} value={ratingMeta?.specific_rating_count?.[1]} data-rating="2"></progress>
                                    <span className="progress-star" data-rating="2"><img src={star} alt="star" /></span>
                                </div>
                                <div className="index-count">{ratingMeta?.specific_rating_count?.[1] ?? 0}</div>
                            </div>
                            <div className="index-flexRow index-ratingBarContainer">
                                <div className="index-rating">
                                    <span className="index-ratingLevel">Bad</span>
                                </div>
                                <div className="progress-wrapper" style={{
                                    '--progress-percent': ratingMeta?.total_ratings_count > 0 ? `${(ratingMeta?.specific_rating_count?.[0] / ratingMeta?.total_ratings_count) * 100}%` : '0%'
                                }}>
                                    <progress min="0" max={ratingMeta?.total_ratings_count} value={ratingMeta?.specific_rating_count?.[0]} data-rating="1"></progress>
                                    <span className="progress-star" data-rating="1"><img src={star} alt="star" /></span>
                                </div>
                                <div className="index-count">{ratingMeta?.specific_rating_count?.[0] ?? 0}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="thumbnails-container">
                    <div className="image-thumb-wrapper">
                        <div className="headline">Customer Photos ({
                            ratingMeta?.reviews_data?.length
                                ? ratingMeta.reviews_data.reduce((total, obj) => {
                                    // Add the length of images array in the current object, defaulting to 0
                                    return total + (obj?.images?.length || 0);
                                }, 0) // Start with an initial total of 0
                                : 0
                        })</div>

                        {/* <div className="images-container">
                            <div className="image-container">
                                <div className="image">
                                    <img src={img1} alt="img1" />
                                </div>
                            </div>
                            <div className="image-container">
                                <div className="image">
                                    <img src={img1} alt="img1" />
                                </div>
                            </div>
                            <div className="image-container">
                                <div className="image">
                                    <img src={img1} alt="img1" />
                                </div>
                            </div>
                            <div className="image-container">
                                <div className="image">
                                    <img src={img1} alt="img1" />
                                    <span className="more-images-count">+15</span>
                                    <div className="skeleton"></div>
                                </div>
                            </div>
                        </div> */}

                        <div className="images-container images-container--limited">
                            {allPhotosFlat.length > 0 ? (
                                customerPhotosToShow.map((item, i) => (
                                    <div
                                        key={`image-${item.reviewIndex}-${item.imageIndex}`}
                                        className={`image-container${i === CUSTOMER_PHOTOS_DISPLAY_LIMIT - 1 && totalCustomerPhotos > CUSTOMER_PHOTOS_DISPLAY_LIMIT ? ' image-container--has-more' : ''}`}
                                        onClick={() => openImageModal(allPhotosFlat, i)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && openImageModal(allPhotosFlat, i)}
                                    >
                                        <div className="image">
                                            <img src={ensureMediaUrl(item.url)} alt={`Customer photo ${i + 1}`} />
                                            {i === CUSTOMER_PHOTOS_DISPLAY_LIMIT - 1 && totalCustomerPhotos > CUSTOMER_PHOTOS_DISPLAY_LIMIT && (
                                                <span className="more-images-count">+{totalCustomerPhotos - CUSTOMER_PHOTOS_DISPLAY_LIMIT} more</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="reviews-empty-state">No customer photos yet</p>
                            )}
                        </div>
                    </div>

                    {/* <div className="image-only-modal-container">
                    <div className="modal-container">
                        <header>
                            <span>X</span>
                            All Customers Photos
                        </header>

                        <div className="images-wrapper">
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                            <div className="image">
                                <img src={img1} alt="img1" />
                            </div>
                        </div>

                    </div>
                </div> */}

                    {/* <div className="image-reviews-modal-container">
                    <div className="modal-container">

                        <div className="image-wrapper">
                            <span>X</span>
                            <div className="image"><img src={img1} alt="img1" /></div>
                            <div className="button-container">
                                <div className="prev"><img src={leftArrowWhite} /></div>
                                <div className="next"><img src={rightArrowWhite} /></div>
                            </div>
                            <div className="thumb-wrapper">
                                <div className="thumb-container">
                                    <img src={img1} alt="img1" />
                                </div>
                            </div>
                        </div>

                        <div className="review-wrapper">
                            <div className="user-wrapper">
                                <div className="user-review-main">

                                    <div className="startWrapper">
                                        <div>
                                            <div>5</div>
                                            <div><img src={whiteStar} /></div>
                                        </div>
                                    </div>

                                    <div className="reviewTextWrapper">
                                        Great fit and good cotton fabric, value for money.
                                    </div>
                                </div>

                                <div className="user-review-footer">
                                    <div className="user-review-left">
                                        <span>Avneesh Kumar Pal</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div> */}

                    <div className="review-headline">
                        Customer Reviews ({ratingMeta?.reviews_data?.length || 0})
                    </div>

                    <div className="user-review-container">
                        {(ratingMeta?.reviews_data || []).map((ratings_data, reviewIndex) => (
                                    <div key={ratings_data?._id || reviewIndex} className="user-review-wrapper">
                                        <div className="main">
                                            <div className="startWrapper">
                                                <div data-rating={ratings_data?.rating ?? 5}>
                                                    <div>{ratings_data?.rating}</div>
                                                    <div><img src={whiteStar} alt="" /></div>
                                                </div>
                                            </div>
                                            <div className="reviewTextRwapper">
                                                {ratings_data?.comment}
                                            </div>
                                            <div className="images-container">
                                                <div className="thumb-images">
                                                    {(ratings_data?.images?.slice(0, 6) || []).map((image, imageIndex) => {
                                                        const reviewImages = (ratings_data?.images || []).map(ensureMediaUrl);
                                                        return (
                                                        <div
                                                            key={imageIndex}
                                                            className="images"
                                                            onClick={() => openImageModal(reviewImages, imageIndex)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => e.key === 'Enter' && openImageModal(reviewImages, imageIndex)}
                                                        >
                                                            <img src={ensureMediaUrl(image)} alt={`Customer photo ${imageIndex + 1}`} />
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                        </div>

                                        <div className="footer">
                                            <div className="user-review-left">
                                                <span>Myntra Customer</span>
                                            </div>
                                        </div>
                                    </div>
                        ))}
                    </div >
                </div>
                {loading && <div className="loader"></div>}


                <div ref={lastProductRef}></div>
            </div>

            {imageModalOpen && modalCurrentUrl && (
                <div className="web-reviews-image-modal" onClick={closeImageModal}>
                    <div className="web-reviews-image-modal__inner" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="web-reviews-image-modal__close"
                            onClick={closeImageModal}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <div className="web-reviews-image-modal__main">
                            <img src={modalCurrentUrl} alt="" />
                        </div>
                        <div className="web-reviews-image-modal__nav">
                            <button
                                type="button"
                                className="web-reviews-image-modal__prev"
                                onClick={(e) => { e.stopPropagation(); if (canModalPrev) modalPrev(); }}
                                disabled={!canModalPrev}
                                aria-label="Previous image"
                            >
                                <img src={leftArrowWhite} alt="" />
                            </button>
                            <button
                                type="button"
                                className="web-reviews-image-modal__next"
                                onClick={(e) => { e.stopPropagation(); if (canModalNext) modalNext(); }}
                                disabled={!canModalNext}
                                aria-label="Next image"
                            >
                                <img src={rightArrowWhite} alt="" />
                            </button>
                        </div>
                        <div className="web-reviews-image-modal__counter">
                            {modalCurrentIndex + 1} / {modalImagesList.length}
                        </div>
                        {modalImagesList.length > 1 && (() => {
                            const total = modalImagesList.length;
                            const maxThumbs = 6;
                            const start = total <= maxThumbs ? 0 : Math.max(0, Math.min(modalCurrentIndex - 1, total - maxThumbs));
                            const end = Math.min(start + maxThumbs, total);
                            const thumbIndices = Array.from({ length: end - start }, (_, idx) => start + idx);
                            return (
                                <div className="web-reviews-image-modal__thumbs">
                                    {thumbIndices.map((idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={`web-reviews-image-modal__thumb ${idx === modalCurrentIndex ? 'is-active' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); setModalCurrentIndex(idx); }}
                                            aria-label={`Image ${idx + 1}`}
                                            aria-current={idx === modalCurrentIndex}
                                        >
                                            <img src={modalImagesList[idx]} alt="" />
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}
